import { hasSizeOrderName, isExpressSizeOrderItem, isSizeOrderItem, type SizeOrderItem } from "../shared/size-order-item";

export type OrderListSkuItem = SizeOrderItem;

export interface RepairOrderListSkuItem extends OrderListSkuItem {
  properties: Array<{ name: string; value: string }>;
}

const onlineTargetSuffix = /(?:98|99)$/;
const posExcludedSkuParts = ["option", "ka", "myfb", "cs-8", "b40", "repair", "pos-op"];
const dailyHiddenSkuParts = ["repair", "option", "myfb", "pos-op", "csmz"];
const repairPropertyName = "配送前ドレス裾直し";

function normalizedSourceName(sourceName: string): string {
  return sourceName.trim().toLowerCase();
}

export function filterOrderListSkuItems<T extends OrderListSkuItem>(
  sourceName: string,
  items: T[],
): T[] {
  const normalizedSource = normalizedSourceName(sourceName);

  if (
    normalizedSource === "web"
    || normalizedSource === "shopify_draft_order"
    || normalizedSource === "draft_order"
  ) {
    return items.filter(isSizeOrderItem);
  }

  if (normalizedSource === "pos") {
    return items.filter((item) => {
      const sku = item.sku.trim().normalize("NFKC").toLowerCase();
      return !isExpressSizeOrderItem(item) && (hasSizeOrderName(item) || (sku.length > 0 && !posExcludedSkuParts.some((part) => sku.includes(part))));
    });
  }

  return items.filter((item) => !isExpressSizeOrderItem(item) && (item.sku.trim().length > 0 || hasSizeOrderName(item)));
}

export function orderListItemsForWorkspace<T extends OrderListSkuItem>(
  workspace: "size" | "all",
  sourceName: string,
  items: T[],
): T[] {
  return workspace === "all"
    ? items.filter((item) => !isDailyOrderListHiddenSku(item.sku))
    : filterOrderListSkuItems(sourceName, items);
}

export function isDailyOrderListHiddenSku(sku: string): boolean {
  const normalizedSku = sku.trim().normalize("NFKC").toLowerCase();
  return onlineTargetSuffix.test(normalizedSku)
    || dailyHiddenSkuParts.some((part) => normalizedSku.includes(part));
}

export function isDailyOrderListHiddenOrder<T extends OrderListSkuItem>(items: T[]): boolean {
  if (items.length === 0) return false;
  const normalizedSkus = items.map((item) => item.sku.trim().normalize("NFKC").toLowerCase());
  const hasTargetSuffix = normalizedSkus.some((sku) => onlineTargetSuffix.test(sku));
  const hasDc1113Sku = normalizedSkus.some((sku) => sku.includes("dc1113"));
  return normalizedSkus.every((sku) =>
    isDailyOrderListHiddenSku(sku)
    || (hasTargetSuffix && hasDc1113Sku && sku.includes("dc1113"))
  );
}

export function shouldDisplayDailyOrderListOrder<T extends OrderListSkuItem>(items: T[]): boolean {
  return !isDailyOrderListHiddenOrder(items);
}

export function filterRepairOrderListSkuItems<T extends RepairOrderListSkuItem>(items: T[]): T[] {
  const propertyItems = items.filter((item) => item.properties.some(
    (property) => property.name.normalize("NFKC").trim() === repairPropertyName,
  ));
  if (propertyItems.length > 0) return propertyItems;

  return items.filter((item) => {
    const sku = item.sku.trim().normalize("NFKC").toLowerCase();
    return sku.length > 0 && !posExcludedSkuParts.some((part) => sku.includes(part));
  });
}
export interface OrderListSizeItem {
  variantTitle: string | null;
  properties: Array<{ name: string; value: string }>;
}

export function orderListItemSize(item: OrderListSizeItem): string {
  const sizeProperty = item.properties.find((property) => {
    const name = property.name.normalize("NFKC").trim().toLowerCase();
    return name === "サイズ" || name === "size";
  });
  const propertyValue = sizeProperty?.value.normalize("NFKC").trim() ?? "";
  if (propertyValue) return propertyValue;
  return item.variantTitle?.normalize("NFKC").trim() ?? "";
}
